import { useState, useMemo, useCallback } from 'react';
import { api, toastError, uploadReferenceImage } from '../utils/api';
import { useToast } from './useToast';
import type {
  ExtractStoryElementsResponse,
  FormatPreset,
  ReferenceImage,
  StoryElement,
  StoryElementKind,
  StylePreset,
  UpdateProjectRequest,
} from '@historyjki/shared';
import type { useProject } from './useProject';

type ProjectHookReturn = ReturnType<typeof useProject>;
type Stage = NonNullable<UpdateProjectRequest['stage']>;

export type DefineTab =
  | 'style'
  | 'format'
  | 'characters'
  | 'places'
  | 'objects'
  | 'typography';

export interface StoryElementSuggestion {
  kind: StoryElementKind;
  name: string;
  description: string;
  visual_description: Record<string, unknown>;
  status: 'pending' | 'accepted' | 'rejected';
}

export interface DefineElementCounts {
  character: number;
  place: number;
  object: number;
  total: number;
}

function kindForTab(tab: DefineTab): StoryElementKind | null {
  if (tab === 'characters') return 'character';
  if (tab === 'places') return 'place';
  if (tab === 'objects') return 'object';
  return null;
}

/** Pull a handful of short visual traits out of a stored visual_description JSON. */
function getTraits(visualDescription: string | null): string[] {
  if (!visualDescription) return [];
  try {
    const parsed = JSON.parse(visualDescription) as Record<string, unknown>;
    return Object.values(parsed)
      .filter((v): v is string => typeof v === 'string' && v.length > 0 && v.length < 60)
      .slice(0, 5);
  } catch {
    return [];
  }
}

/**
 * Business logic for the Define stage: AI story-element extraction + the
 * suggestion lifecycle, story-element CRUD, reference-image management, and
 * project settings (style / themes / format / typography). The component keeps
 * presentation state (active tab, the element being edited, new-element form
 * fields, gallery/refine modal state).
 *
 * `activeTab` is passed in so tab-scoped derived data (current kind, elements
 * and suggestions for the tab) is computed here rather than inline in JSX.
 */
export function useDefineStage(projectHook: ProjectHookReturn, activeTab: DefineTab) {
  const {
    project,
    updateProject,
    addStoryElement,
    updateStoryElement,
    deleteStoryElement,
    refetch,
  } = projectHook;
  const { showToast } = useToast();

  const [extracting, setExtracting] = useState(false);
  const [suggestions, setSuggestions] = useState<StoryElementSuggestion[]>([]);
  const [deletingRefImage, setDeletingRefImage] = useState<string | null>(null);
  const [analyzingStyle, setAnalyzingStyle] = useState(false);

  const storyElements = useMemo(() => project?.story_elements || [], [project]);
  const selectedStyle = project?.style_preset || null;
  const selectedFormat = project?.format_preset || 'square-8x8';

  const counts = useMemo<DefineElementCounts>(
    () =>
      storyElements.reduce(
        (acc, el) => {
          acc[el.kind] += 1;
          acc.total += 1;
          return acc;
        },
        { character: 0, place: 0, object: 0, total: 0 } as DefineElementCounts,
      ),
    [storyElements],
  );

  const currentKind = kindForTab(activeTab);
  const tabElements = useMemo(
    () => (currentKind ? storyElements.filter((e) => e.kind === currentKind) : []),
    [currentKind, storyElements],
  );
  const kindSuggestions = useMemo(
    () =>
      currentKind
        ? suggestions.filter((s) => s.kind === currentKind && s.status === 'pending')
        : [],
    [currentKind, suggestions],
  );
  const pendingSuggestions = useMemo(
    () => suggestions.filter((s) => s.status === 'pending'),
    [suggestions],
  );

  const getRefImages = useCallback(
    (ownerType: string, ownerId: string): ReferenceImage[] =>
      (project?.reference_images || []).filter(
        (img) => img.owner_type === ownerType && img.owner_id === ownerId,
      ),
    [project],
  );

  const findElement = useCallback(
    (id: string | null): StoryElement | null =>
      id ? storyElements.find((e) => e.id === id) ?? null : null,
    [storyElements],
  );

  const extract = useCallback(async () => {
    if (!project) return;
    setExtracting(true);
    try {
      const data = await api.post<ExtractStoryElementsResponse>(
        `/projects/${project.id}/ai/extract-story-elements`,
        {},
      );
      setSuggestions(data.elements.map((e) => ({ ...e, status: 'pending' as const })));
      showToast(`Found ${data.elements.length} story elements`, 'success');
    } catch (err) {
      toastError(showToast, err, 'Failed to extract story elements');
    } finally {
      setExtracting(false);
    }
  }, [project, showToast]);

  const markSuggestion = useCallback(
    (index: number, status: StoryElementSuggestion['status']) => {
      setSuggestions((prev) => {
        const next = [...prev];
        if (next[index]) next[index] = { ...next[index], status };
        return next;
      });
    },
    [],
  );

  /** Accept a suggestion → persist it as a story element. Returns the kind so
   *  the caller can switch to its tab, or null if it could not be added. */
  const acceptSuggestion = useCallback(
    async (index: number): Promise<StoryElementKind | null> => {
      const suggestion = suggestions[index];
      if (!suggestion || suggestion.status !== 'pending') return null;
      const added = await addStoryElement({
        kind: suggestion.kind,
        name: suggestion.name,
        description: suggestion.description,
        visual_description: suggestion.visual_description,
        sort_order: storyElements.length,
      });
      if (!added) return null;
      markSuggestion(index, 'accepted');
      return suggestion.kind;
    },
    [suggestions, addStoryElement, storyElements.length, markSuggestion],
  );

  const rejectSuggestion = useCallback(
    (index: number) => markSuggestion(index, 'rejected'),
    [markSuggestion],
  );

  /** Validate + persist a manually entered story element. Returns success. */
  const addElement = useCallback(
    async (data: { kind: StoryElementKind; name: string; description: string }): Promise<boolean> => {
      if (!data.name.trim()) {
        showToast('Name is required', 'warning');
        return false;
      }
      const added = await addStoryElement({
        kind: data.kind,
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        sort_order: storyElements.length,
      });
      if (!added) return false;
      showToast('Story element added', 'success');
      return true;
    },
    [addStoryElement, storyElements.length, showToast],
  );

  const deleteRefImage = useCallback(
    async (imageId: string) => {
      setDeletingRefImage(imageId);
      try {
        await api.delete(`/upload/${imageId}`);
        await refetch();
        showToast('Reference image removed', 'success');
      } catch (err) {
        toastError(showToast, err, 'Failed to remove image');
      } finally {
        setDeletingRefImage(null);
      }
    },
    [refetch, showToast],
  );

  // Only the preset moves — style_prompt_fragment (the custom style text,
  // typed or derived from a reference) deliberately survives switching to a
  // concrete preset and back; resolveStylePromptFragment ignores it while a
  // preset is active.
  const selectStyle = useCallback(
    async (styleValue: string) => {
      await updateProject({ style_preset: styleValue as StylePreset });
    },
    [updateProject],
  );

  const saveCustomStyle = useCallback(
    async (value: string) => {
      await updateProject({ style_prompt_fragment: value });
    },
    [updateProject],
  );

  const uploadStyleRef = useCallback(
    async (files: File[]) => {
      if (!project) return;
      const file = files[0];
      if (!file) return;
      try {
        await uploadReferenceImage('project', project.id, file);
        await refetch();
        showToast('Style reference image uploaded', 'success');
        // Claude analysis of the reference runs asynchronously (server-side
        // waitUntil, ~2-5s after upload) and writes style_prompt_fragment.
        // Poll a few times so the derived style text appears in the textarea.
        setAnalyzingStyle(true);
        for (let i = 0; i < 4; i++) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await refetch();
        }
      } catch (err) {
        toastError(showToast, err, 'Failed to upload image');
      } finally {
        setAnalyzingStyle(false);
      }
    },
    [project, refetch, showToast],
  );

  /** Re-derive the custom style text from the uploaded style reference —
   *  used after refining/replacing the reference, or when the author wants
   *  a fresh read after hand-editing the text too far. */
  const reanalyzeStyle = useCallback(async () => {
    if (!project) return;
    setAnalyzingStyle(true);
    try {
      await api.post(`/projects/${project.id}/reanalyze-style`, {});
      await refetch();
      showToast('Style re-analyzed from the reference image', 'success');
    } catch (err) {
      toastError(showToast, err, 'Failed to re-analyze style');
    } finally {
      setAnalyzingStyle(false);
    }
  }, [project, refetch, showToast]);

  const selectFormat = useCallback(
    async (format: FormatPreset) => {
      await updateProject({ format_preset: format });
    },
    [updateProject],
  );

  const updateProjectData = useCallback(
    (data: UpdateProjectRequest) => {
      void updateProject(data);
    },
    [updateProject],
  );

  const goToStage = useCallback(
    (stage: Stage) => {
      void updateProject({ stage });
    },
    [updateProject],
  );

  const continueToCompose = useCallback(async () => {
    await updateProject({ stage: 'compose' });
  }, [updateProject]);

  return {
    project,
    storyElements,
    selectedStyle,
    selectedFormat,
    counts,
    currentKind,
    tabElements,
    kindSuggestions,
    pendingSuggestions,
    suggestions,
    extracting,
    deletingRefImage,
    analyzingStyle,
    getRefImages,
    getTraits,
    findElement,
    extract,
    acceptSuggestion,
    rejectSuggestion,
    addElement,
    deleteRefImage,
    selectStyle,
    saveCustomStyle,
    uploadStyleRef,
    reanalyzeStyle,
    selectFormat,
    updateProjectData,
    updateStoryElement,
    deleteStoryElement,
    goToStage,
    continueToCompose,
    refetch,
  };
}
